import { DirectionCode, isDirectionCode, FileWithPath, PhotoDraft, PhotoSection, SECTIONS } from "./photoModel";

/** Extracts { direction, order } from a filename's trailing `_<num>_<DIR>` suffix. */
export function parseDirectionSuffix(
  fileName: string
): { direction: DirectionCode | null; order: number | null } {
  const base = fileName.replace(/\.[^.]+$/, ""); // drop extension
  const tokens = base.split("_");
  const last = (tokens[tokens.length - 1] ?? "").toUpperCase();
  if (!isDirectionCode(last)) return { direction: null, order: null };
  const prev = tokens[tokens.length - 2] ?? "";
  const order = /^\d{1,3}$/.test(prev) ? Number(prev) : null;
  return { direction: last, order };
}

function getPath(file: FileWithPath): string {
  return (file.path ?? file.name).replace(/^\/+/, "");
}

/** Builds a PhotoDraft from a dropped file, or null when the file is loose. */
export function parsePhotoDraft(
  file: FileWithPath,
  index: number,
  previewUrl: string
): PhotoDraft | null {
  const path = getPath(file);
  const segments = path.split("/").filter(Boolean);
  if (segments.length < 4) return null;
  const barcode = segments[0].trim();
  const matCode = segments[1].trim();
  const section = segments[2].trim().toLowerCase() as PhotoSection;
  const fileName = segments[segments.length - 1];
  if (!barcode || !matCode) return null;
  if (!SECTIONS.includes(section)) return null;

  const { direction, order } = parseDirectionSuffix(fileName);
  const ext = (fileName.split(".").pop() ?? "png").toLowerCase();

  return {
    id: path,
    file,
    previewUrl,
    ext,
    barcode,
    matCode,
    section,
    direction,
    order: order ?? index + 1,
    isMain: false,
    origin: { section, direction, fileName },
    parseState: direction ? "confident" : "unknown",
  };
}

export function formatFileName(p: {
  barcode: string;
  matCode: string;
  direction: string;
  ext: string;
  number: number | null;
}): string {
  const num = p.number === null ? "" : `_${p.number}`;
  return `${p.barcode}_${p.matCode}${num}_${p.direction}.${p.ext}`;
}

/** Derives { fileName, key } for every draft that has a direction, keyed by id. */
export function deriveNames(
  drafts: PhotoDraft[]
): Record<string, { fileName: string; key: string }> {
  const groups = new Map<string, PhotoDraft[]>();
  for (const d of drafts) {
    if (!d.direction) continue;
    const gk = `${d.barcode}|${d.matCode}|${d.section}|${d.direction}`;
    (groups.get(gk) ?? groups.set(gk, []).get(gk)!).push(d);
  }
  const out: Record<string, { fileName: string; key: string }> = {};
  for (const group of Array.from(groups.values())) {
    const sorted = [...group].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
    const numbered = sorted.length > 1;
    sorted.forEach((d, i) => {
      const fileName = formatFileName({
        barcode: d.barcode,
        matCode: d.matCode,
        direction: d.direction as string,
        ext: d.ext,
        number: numbered ? i + 1 : null,
      });
      out[d.id] = { fileName, key: `original/${d.barcode}/${d.matCode}/${d.section}/${fileName}` };
    });
  }
  return out;
}
